<template>
  <div class="date-scope">
    <m-select
      v-if="!hideOp"
      v-model:value="selectedType"
      :items="intervalTypes"
      :read-only="readOnly"
      :disabled="disabled"
      class="_op"
    />
    <m-select
      v-if="hasDynamicValues && !isEmptyType"
      v-model:value="selectedDynamicType"
      :items="dynamicTypes"
      :read-only="readOnly"
      :disabled="disabled"
      class="_op"
    />
    <m-input-number
      v-if="showNumField"
      v-model:value="selectedNumber"
      :read-only="readOnly"
      :disabled="disabled"
      nullable
      :min="0"
      class="_number"
    />
    <template v-if="showDatePickers">
      <div
        v-if="showDatePicker"
        class="_date-picker-wrapper"
      >
        <m-date-picker
          :value="fromISO(selectedDate)"
          class="_date-picker"
          full-width
          :placeholder="$t('general.empty')"
          :read-only="readOnly"
          :disabled="disabled"
          :date-time="DateTime"
          clearable
          placement="bottomCenter"
          @input="updateSelectedDate"
        />
      </div>
      <m-date-picker
        v-if="isBetween"
        v-model:value="range"
        :placeholder="$t('general.empty')"
        :read-only="readOnly"
        :disabled="disabled"
        :date-time="DateTime"
        range
        clearable
      />
    </template>
  </div>
</template>

<script>
import useScopeItem from '@/composables/filter/scope-item';
import {
  BETWEEN,
  EMPTY,
  EXACT_DATE,
  GT,
  LAST_X_DAYS,
  LT,
  NEXT_X_DAYS,
  NOT_EMPTY,
} from '@/lib/constants';
import { DateTime } from 'luxon';
import { copy } from 'shared/lib/copy';
import { fromISO, toISO } from 'shared/lib/time';
import { getType } from '@/lib/filter/dynamic-date';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { propertyType, userScopeOperator } from 'shared/constants.json';

export default {
  name: 'DateScope',
  props: {
    value: {
      type: Object,
      required: true,
    },
    hasDynamicValues: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideOp: {
      type: Boolean,
      default: false,
    },
    hideIsEmpty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:value', 'change', 'change-not'],
  setup(props) {
    const { operatorTypes: getOperatorTypes } = useScopeItem();
    const intervalTypes = getOperatorTypes(propertyType.date, { hideEmpty: props.hideIsEmpty });
    return { intervalTypes };
  },
  data() {
    return {
      startDate: null,
      endDate: null,
      selectedDate: null,
      selectedType: LT,
      selectedNumber: 7,
      selectedDynamicType: EXACT_DATE,
      DateTime,
      fromISO,
    };
  },
  computed: {
    dynamicTypes() {
      return this.intervalTypes.find((t) => t.value === this.selectedType)?.dynamicTypes ?? [];
    },
    range: {
      get() {
        return { startDate: this.startDate, endDate: this.endDate };
      },
      set({ startDate, endDate }) {
        this.startDate = startDate;
        this.endDate = endDate;
      },
    },
    scope() {
      return {
        ...this.value,
        timeRange: this.timeRange,
      };
    },
    timeRange() {
      const { start, end } = this.rangeByTypes(this.selectedType, this.selectedDynamicType);
      return {
        start,
        end,
        type: this.selectedType,
        dynamicType: this.selectedDynamicType,
        amount: this.selectedNumber,
      };
    },
    isBetween() {
      return this.selectedType === BETWEEN;
    },
    isEmptyType() {
      return [EMPTY, NOT_EMPTY].includes(this.selectedType);
    },
    showDatePickers() {
      if (!this.hasDynamicValues) {
        return !this.isEmptyType;
      }
      return this.selectedDynamicType === EXACT_DATE && !this.isEmptyType;
    },
    showNumField() {
      return [LAST_X_DAYS, NEXT_X_DAYS].includes(this.selectedDynamicType) && !this.isEmptyType;
    },
    showDatePicker() {
      return [GT, LT].includes(this.selectedType);
    },
  },
  methods: {
    updateSelectedDate(date) {
      this.selectedDate = toISO(date);
    },
    rangeByTypes(type, dynamicType) {
      if (dynamicType !== EXACT_DATE) {
        return { start: null, end: null };
      }
      switch (type) {
        case LT:
          return { start: null, end: DateTime.fromISO(this.selectedDate).startOf('day') };
        case GT:
          return { start: DateTime.fromISO(this.selectedDate).endOf('day'), end: null };
        case BETWEEN:
          return { start: DateTime.fromISO(this.startDate).toISO(), end: DateTime.fromISO(this.endDate).toISO() };
        default:
          return { start: null, end: null };
      }
    },
    init() {
      this.selectedDate = DateTime.local().toISO();
      this.selectedType = LT;
      this.selectedDynamicType = EXACT_DATE;
      this.selectedNumber = 7;
      if (this.hasDynamicValues) {
        this.selectedType = BETWEEN;
        this.selectedDynamicType = LAST_X_DAYS;
      }
      this.emitUpdate(this.scope);
    },
    setValues(timeRange) {
      const type = getType(timeRange);
      this.selectedType = type;

      if (type === BETWEEN) {
        this.startDate = DateTime.fromISO(timeRange.start).toISODate();
        this.endDate = DateTime.fromISO(timeRange.end).toISODate();
      }
      if (type === LT) {
        this.selectedDate = timeRange.end;
      }
      if (type === GT) {
        this.selectedDate = timeRange.start;
      }

      if (this.hasDynamicValues) {
        this.selectedNumber = timeRange.amount;
        this.selectedDynamicType = timeRange.dynamicType;
      }
    },
    updateValues() {
      if (this.value === null || isNullOrUndefined(this.value.timeRange)) {
        this.init();
        return;
      }
      this.setValues(this.value.timeRange);
    },
    emitUpdate(val) {
      this.$emit('input', val);
      this.$emit('update:value', val);
      this.$emit('change', val);
      this.$emit('change-not', userScopeOperator.and);
    },
  },
  watch: {
    scope: {
      handler(newVal, oldVal) {
        if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
          return;
        }

        const cp = copy(newVal);
        this.emitUpdate(cp);
      },
      deep: true,
    },
    value: {
      handler() {
        this.updateValues();
      },
      deep: true,
    },
  },
  created() {
    this.updateValues();
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .date-scope {
    display: flex;

    ._op {
      margin-right: .4rem;
    }

    ._date-picker-wrapper {
      ._date-picker {
        min-width: 13rem;
      }
    }

    ._number {
      width: 6rem;
    }
  }
</style>
